import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useState,
	type ChangeEvent,
	type FocusEvent,
	type InputHTMLAttributes,
	type LabelHTMLAttributes,
	type Ref,
} from 'react'

import { ErrorList } from '#app/components/form/error-list.tsx'
import { type ListOfErrors } from '#app/components/form/types.ts'
import { Label } from '#app/components/ui/label.tsx'
import { PhoneInput } from '#app/components/ui/phone-input.tsx'
import { cn } from '#app/utils/misc.tsx'

const sanitizeDigits = (value: string): string => value.replace(/\D/g, '')

type PhoneInputProps = InputHTMLAttributes<HTMLInputElement> & {
	ref?: Ref<HTMLInputElement>
}

type PhoneFieldProps = {
	labelProps: LabelHTMLAttributes<HTMLLabelElement>
	inputProps?: PhoneInputProps
	descriptionProps?: LabelHTMLAttributes<HTMLParagraphElement>
	errors?: ListOfErrors
	className?: string
}

export function PhoneField({
	labelProps,
	inputProps,
	descriptionProps,
	errors,
	className,
}: PhoneFieldProps) {
	const {
		id: providedId,
		name: providedName,
		value,
		defaultValue,
		onChange,
		onBlur,
		required,
		disabled,
		placeholder,
		className: inputClassName,
		ref,
		...restInput
	} = inputProps ?? {}

	const isControlled = value !== undefined

	const initialNumeric = useMemo(() => {
		const raw =
			typeof value === 'string'
				? value
				: typeof defaultValue === 'string'
					? defaultValue
					: ''
		return sanitizeDigits(raw)
	}, [value, defaultValue])

	const [digits, setDigits] = useState(initialNumeric)

	useEffect(() => {
		if (isControlled) {
			setDigits(initialNumeric)
		}
	}, [initialNumeric, isControlled])

	const fallbackId = useId()
	const id = providedId ?? fallbackId
	const name = providedName ?? id
	const errorId = errors?.length ? `${id}-error` : undefined

	const emitChange = useCallback(
		(nextDigits: string) => {
			if (typeof onChange !== 'function') return
			const target = { value: nextDigits, name } as unknown as HTMLInputElement
			const synthetic = {
				target,
				currentTarget: target,
			} as unknown as ChangeEvent<HTMLInputElement>
			onChange(synthetic)
		},
		[name, onChange],
	)

	const emitBlur = useCallback(
		(currentDigits: string) => {
			if (typeof onBlur !== 'function') return
			const target = {
				value: currentDigits,
				name,
			} as unknown as HTMLInputElement
			const synthetic = {
				target,
				currentTarget: target,
			} as unknown as FocusEvent<HTMLInputElement>
			onBlur(synthetic)
		},
		[name, onBlur],
	)

	const handlePhoneChange = useCallback(
		(fullValue: string) => {
			const nextDigits = sanitizeDigits(fullValue)
			if (!isControlled) {
				setDigits(nextDigits)
			}
			emitChange(nextDigits)
		},
		[emitChange, isControlled],
	)

	const description = descriptionProps ? (
		<p
			{...descriptionProps}
			className={cn(
				'text-muted-foreground text-xs',
				descriptionProps.className,
			)}
		/>
	) : null

	const phoneValue = digits ? `+${digits}` : ''

	const handleContainerBlur = useCallback(
		(event: FocusEvent<HTMLDivElement>) => {
			const related = event.relatedTarget as Node | null
			if (related && event.currentTarget.contains(related)) return
			emitBlur(digits)
		},
		[digits, emitBlur],
	)

	return (
		<div className={cn('space-y-1.5', className)} onBlur={handleContainerBlur}>
			<Label htmlFor={`${id}-display`} {...labelProps} />
			<PhoneInput
				id={`${id}-display`}
				value={phoneValue}
				onChange={handlePhoneChange}
				disabled={disabled}
				required={required}
				placeholder={placeholder}
				inputClassName={inputClassName}
				mode="stacked"
			/>
			{description}
			<input
				{...restInput}
				ref={ref}
				type="hidden"
				id={id}
				name={name}
				value={digits}
				readOnly
				aria-invalid={errorId ? 'true' : undefined}
				aria-describedby={errorId}
			/>
			<div className="min-h-[16px] pt-1 pl-4">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
